import { useCallback, useEffect, useRef, useState } from "react";
import { Pencil, X } from "lucide-react";
import { TextField, Text, Button, ButtonVariant } from "@merge-api/merge-javascript-shared";

type EditableTextProps = {
  originalText: string;
  onSave: (newText: string, onError?: () => void) => void;
  textClassName?: string;
  hasEditShortcut?: boolean;
};

export const EditableText = ({ originalText, onSave, hasEditShortcut }: EditableTextProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [text, setText] = useState<string>(originalText);
  const ref = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isEditing && hasEditShortcut) {
        if (event.metaKey && event.key === "e") {
          setIsEditing(true);
          ref.current?.focus();
          event.preventDefault();
        }
      }
    },
    [isEditing, hasEditShortcut]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Update text if originalText changes
  useEffect(() => {
    if (text != originalText) {
      setText(originalText);
      setIsEditing(false);
    }
  }, [originalText]);

  const resetTextEdit = () => {
    setIsEditing(false);
    setText(originalText);
  };

  const onSaveNewText = () => {
    onSave(text, resetTextEdit);
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <div className="flex flex-row items-center justify-between w-full gap-x-1.5">
          <div className="flex-grow-1">
            <TextField
              ref={ref}
              className="w-full"
              tabIndex={0}
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  resetTextEdit();
                }
                if (e.key === "Enter") {
                  onSaveNewText();
                }
              }}
              onBlur={() => onSaveNewText()}
            />
          </div>
          <Button
            size="sm"
            variant={ButtonVariant.IconShadowHidden}
            onClick={() => resetTextEdit()}
          >
            <X size={16} />
          </Button>
        </div>
      ) : (
        <div
          className="flex flex-row items-center justify-between hover:cursor-pointer gap-x-1.5"
          onClick={() => setIsEditing(true)}
        >
          <Text variant="h6">{text}</Text>

          <Button
            size="sm"
            variant={ButtonVariant.IconShadowHidden}
            onClick={() => resetTextEdit()}
          >
            <Pencil size={16} />
          </Button>
        </div>
      )}
    </>
  );
};
